import React, { useState, useEffect, useMemo } from 'react';
import './BetRecords.css';
import { useNavigate } from "react-router-dom";
import InnerHeader from '../../components/InnerHeader';

const BettingRecords = ({ onViewDetails }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('settled');

  const [providers, setProviders] = useState([]);
  const [gameTypes, setGameTypes] = useState([]);
  const [bettingRecords, setBettingRecords] = useState([]);
  const [exchangeRecords, setExchangeRecords] = useState([]);

  const [selectedPlatform, setSelectedPlatform] = useState("All");
  const [selectedType, setSelectedType] = useState("All");
  const [selectedDate, setSelectedDate] = useState("Last 7 days");

  const [loading, setLoading] = useState(true);
  const [displayLimit, setDisplayLimit] = useState(20);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Save scroll position before navigating
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem('bettingRecordsScrollPosition', window.scrollY.toString());
      sessionStorage.setItem('bettingRecordsDisplayLimit', displayLimit.toString());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      sessionStorage.setItem('bettingRecordsScrollPosition', window.scrollY.toString());
      sessionStorage.setItem('bettingRecordsDisplayLimit', displayLimit.toString());
    };
  }, [displayLimit]);

  // Restore scroll position
  useEffect(() => {
    if (!loading && bettingRecords.length > 0) {
      const savedScrollPosition = sessionStorage.getItem('bettingRecordsScrollPosition');
      const savedDisplayLimit = sessionStorage.getItem('bettingRecordsDisplayLimit');

      if (savedDisplayLimit) {
        setDisplayLimit(parseInt(savedDisplayLimit, 10));
      }

      if (savedScrollPosition) {
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedScrollPosition, 10));
        }, 100);
      }
    }
  }, [loading, bettingRecords]);

  const handleRefresh = async () => {
    sessionStorage.removeItem('bettingRecords');
    sessionStorage.removeItem('providers');
    sessionStorage.removeItem('gameTypes');
    sessionStorage.removeItem('bettingRecordsTimestamp');
    sessionStorage.removeItem('bettingRecordsScrollPosition');
    sessionStorage.removeItem('bettingRecordsDisplayLimit');

    await fetchData(true);
  };

  const fetchData = async (forceFresh = false) => {
    const token = localStorage.getItem("token");
    const baseUrl = process.env.REACT_APP_API_URL;

    if (!baseUrl) {
      console.error("REACT_APP_API_URL is missing");
      setLoading(false);
      return;
    }

    if (!token) {
      console.error("Token not found in localStorage");
      setLoading(false);
      return;
    }

    // Cache
    const cachedRecords = sessionStorage.getItem('bettingRecords');
    const cachedProviders = sessionStorage.getItem('providers');
    const cachedGameTypes = sessionStorage.getItem('gameTypes');
    const cacheTimestamp = sessionStorage.getItem('bettingRecordsTimestamp');

    const isCacheValid =
      cacheTimestamp &&
      (Date.now() - parseInt(cacheTimestamp, 10)) < 5 * 60 * 1000;

    if (
      !forceFresh &&
      isCacheValid &&
      cachedRecords &&
      cachedProviders &&
      cachedGameTypes
    ) {
      console.log("Using cached betting records");
      setBettingRecords(JSON.parse(cachedRecords));
      setProviders(JSON.parse(cachedProviders));
      setGameTypes(JSON.parse(cachedGameTypes));
      setLoading(false);
      return;
    }

    setLoading(true);

    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    try {
      const providerUrl = `${baseUrl}/api/get-provider`;
      const typeUrl = `${baseUrl}/api/get-type`;
      const recordUrl = `${baseUrl}/api/betting-record`;
      const exchangeUrl = `${baseUrl}/api/exchange-hub`;

      console.log("Calling APIs:");
      console.log(providerUrl);
      console.log(typeUrl);
      console.log(recordUrl);
      console.log(exchangeUrl);

      const results = await Promise.allSettled([
        fetch(providerUrl, { headers }),
        fetch(typeUrl, { headers }),
        fetch(recordUrl, { headers }),
        fetch(exchangeUrl, { headers }),
      ]);

      const [providersRes, typesRes, recordsRes, exchangeRes] = results;

      // Providers
      if (providersRes.status === "fulfilled") {
        const res = providersRes.value;
        if (res.ok) {
          const data = await res.json();
          const providerList = Array.isArray(data?.data) ? data.data : [];
          setProviders(providerList);
          sessionStorage.setItem('providers', JSON.stringify(providerList));
        } else {
          console.warn("Providers API failed:", res.status);
        }
      } else {
        console.warn("Providers request error:", providersRes.reason);
      }

      // Game Types
      if (typesRes.status === "fulfilled") {
        const res = typesRes.value;
        if (res.ok) {
          const data = await res.json();
          const typeList = Array.isArray(data?.types)
            ? data.types
            : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
            ? data
            : [];
          setGameTypes(typeList);
          sessionStorage.setItem('gameTypes', JSON.stringify(typeList));
        } else {
          console.warn("Types API failed:", res.status);
        }
      } else {
        console.warn("Types request error:", typesRes.reason);
      }

      // Betting Records
      if (recordsRes.status === "fulfilled") {
        const res = recordsRes.value;
        if (res.ok) {
          const data = await res.json();
          console.log("Betting Records Raw Response:", data);

          const recordList = Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.records)
            ? data.records
            : [];

          console.log("Parsed Betting Records:", recordList);

          setBettingRecords(recordList);
          sessionStorage.setItem('bettingRecords', JSON.stringify(recordList));
          sessionStorage.setItem('bettingRecordsTimestamp', Date.now().toString());
        } else {
          const errorText = await res.text();
          console.warn("Betting record API failed:", res.status, errorText);
          setBettingRecords([]);
        }
      } else {
        console.warn("Betting record request error:", recordsRes.reason);
        setBettingRecords([]);
      }

      // Exchange
      if (exchangeRes.status === "fulfilled") {
        const res = exchangeRes.value;
        if (res.ok) {
          const data = await res.json();
          const exchangeList = Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
            ? data
            : [];
          setExchangeRecords(exchangeList);
        } else {
          const errorText = await res.text();
          console.warn("Exchange API failed:", res.status, errorText);
          setExchangeRecords([]);
        }
      } else {
        console.warn("Exchange request error:", exchangeRes.reason);
        setExchangeRecords([]);
      }
    } catch (error) {
      console.error("Unexpected fetchData error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(false);
  }, []);

  const isDateInRange = (date, range) => {
    if (!date) return false;

    const today = new Date();
    const txnDate = new Date(date);

    if (isNaN(txnDate.getTime())) return false;

    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 7);

    if (range === "Today") {
      return txnDate.toDateString() === today.toDateString();
    }

    if (range === "Yesterday") {
      return txnDate.toDateString() === yesterday.toDateString();
    }

    if (range === "Last 7 days") {
      return txnDate >= lastWeek && txnDate <= today;
    }

    return true;
  };

  const filteredRecords = useMemo(() => {
    return bettingRecords.filter((record) => {
      const matchesPlatform =
        selectedPlatform === "All" || record.platform === selectedPlatform;

      const matchesType =
        selectedType === "All" || record.game_type === selectedType;

      const matchesDate = isDateInRange(record.created_at, selectedDate);

      const isSettled = record.action === "betNSettle";
      const matchesTab = activeTab === 'settled' ? isSettled : !isSettled;

      return matchesPlatform && matchesType && matchesDate && matchesTab;
    });
  }, [bettingRecords, selectedPlatform, selectedType, selectedDate, activeTab]);

  useEffect(() => {
    setDisplayLimit(20);
  }, [activeTab, selectedPlatform, selectedType, selectedDate]);

  function getIconSVG(gameType) {
    const icons = {
      Cricket: '🏏',
      Football: '⚽',
      Tennis: '🎾',
      Basketball: '🏀',
      Hockey: '🏒',
      Baseball: '⚾',
      Rugby: '🏉',
      Table: '🎲',
      Exchange: '💱',
      Slot: '🎰',
      slot: '🎰',
      Casino: '🎰',
      casino: '🎰',
      Poker: '🃏',
      poker: '🃏',
    };

    const colors = {
      Cricket: '#FF6B35',
      Football: '#4CAF50',
      Tennis: '#FFC107',
      Basketball: '#FF9800',
      Hockey: '#2196F3',
      Baseball: '#E91E63',
      Rugby: '#9C27B0',
      Table: '#8BC34A',
      Exchange: '#00BCD4',
      Slot: '#FF5722',
      slot: '#FF5722',
      Casino: '#673AB7',
      casino: '#673AB7',
      Poker: '#00BCD4',
      poker: '#00BCD4',
    };

    const emoji = icons[gameType] || '🎮';
    const color = colors[gameType] || '#666';

    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' rx='20' fill='%23333'/%3E%3Ctext x='20' y='25' font-family='Arial' font-size='20' fill='${color.replace('#', '%23')}' text-anchor='middle'%3E${emoji}%3C/text%3E%3C/svg%3E`;
  }

  const displayRecords = filteredRecords.map((record) => {
    const betAmount = parseFloat(record.bet_amount) || 0;
    const winAmount = parseFloat(record.winAmount) || 0;
    const profitLoss = winAmount - betAmount;

    return {
      id: record.id,
      date: new Date(record.created_at).toDateString(),
      sport: record.platform,
      badge: record.game_type,
      turnover: betAmount.toFixed(2),
      icon: getIconSVG(record.game_type),
      gameName: record.game_name || record.game_type,
      result: profitLoss.toFixed(2),
      timestamp: new Date(record.created_at).toLocaleString(),
      details: {
        sport: record.platform,
        totalTurnover: betAmount.toFixed(2),
        transactions: [
          {
            id: record.id,
            name: record.game_name || record.game_type,
            turnover: betAmount.toFixed(2),
            result: profitLoss.toFixed(2),
            timestamp: new Date(record.created_at).toLocaleString(),
            platform_tx_id: record.platform_tx_id,
            round_id: record.round_id,
          },
        ],
      },
    };
  });

  const limitedRecords = displayRecords.slice(0, displayLimit);

  const groupedByDate = limitedRecords.reduce((acc, bet) => {
    if (!acc[bet.date]) acc[bet.date] = [];
    acc[bet.date].push(bet);
    return acc;
  }, {});

  const handleCardClick = (bet) => {
    navigate('/betting-details', { state: { betDetails: bet } });
    if (onViewDetails) onViewDetails(bet);
  };

  const loadMoreRecords = () => {
    if (isLoadingMore || displayLimit >= displayRecords.length) return;

    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayLimit((prev) => prev + 20);
      setIsLoadingMore(false);
    }, 500);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadMoreRecords();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, displayLimit, displayRecords.length]);

  return (
    <div className="bets-container">
      <InnerHeader title="Betting records" />

      <div className="bets-tabs">
        <button
          className={`bets-tab ${activeTab === 'settled' ? 'bets-tab-active' : ''}`}
          onClick={() => setActiveTab('settled')}
        >
          Settled
        </button>
        <button
          className={`bets-tab ${activeTab === 'unsettled' ? 'bets-tab-active' : ''}`}
          onClick={() => setActiveTab('unsettled')}
        >
          Unsettled
        </button>
      </div>

      <div className="bets-filter-section">
        <div className="bets-filter-dropdown">
          <span className="bets-filter-text">{selectedDate}</span>
        </div>

        <div className="bets-filter-actions">
          <button className="bets-filter-icon" onClick={handleRefresh} title="Refresh">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18M7 12h10M11 18h2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bets-loader">
          <div className="bets-spinner"></div>
          <p>Loading betting records...</p>
        </div>
      ) : (
        <>
          {Object.keys(groupedByDate).length > 0 ? (
            <>
              {Object.keys(groupedByDate).map((date) => (
                <div key={date}>
                  <div className="bets-date">{date}</div>

                  {groupedByDate[date].map((bet) => (
                    <div
                      key={bet.id}
                      className="bets-card"
                      onClick={() => handleCardClick(bet)}
                    >
                      <div className="bets-card-left">
                        <div className="bets-icon-wrapper">
                          <img
                            src={bet.icon}
                            alt={bet.sport}
                            className="bets-sport-icon"
                          />
                        </div>

                        <div className="bets-card-info">
                          <span className="bets-sport-name">{bet.sport}</span>
                          <span className="bets-badge">{bet.badge}</span>
                        </div>
                      </div>

                      <div className="bets-card-right">
                        <span className="bets-turnover-label">Turnover</span>
                        <span className="bets-turnover-value">{bet.turnover}</span>
                        <svg className="bets-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M9 18l6-6-6-6" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {displayLimit < displayRecords.length && (
                <div className="bets-load-more">
                  {isLoadingMore ? (
                    <>
                      <div className="bets-spinner-small"></div>
                      <p>Loading more...</p>
                    </>
                  ) : (
                    <button onClick={loadMoreRecords} className="bets-load-more-btn">
                      Load More ({displayRecords.length - displayLimit} remaining)
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="bets-no-data">
              <p style={{ textAlign: 'center', color: '#999', padding: '40px 20px' }}>
                No betting records found
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BettingRecords;